import { useEffect } from 'react'
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Info, Settings } from 'lucide-react'
import type { WorldBuilderInput } from '@creanizer/types'
import { generateCharacterContext, generateEnemyContext, generatePlotContext, generateRelationshipContext, generateWikipediaContext } from '@creanizer/utils'
import { useCharacterStore } from '../../../../../stores/characterStore'
import { usePlotStore } from '../../../../../stores/plotStore'
import { useRelationshipStore } from '../../../../../stores/relationshipStore'
import { useWikipediaStore } from '../../../../../stores/wikipediaStore'
import { useNameStore } from '../../../../../stores/nameStore'
import { useTitleStore } from '../../../../../stores/titleStore'
import { useEnemyStore } from '../../../../../stores/enemyStore'

interface ReferenceModeFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

export function ReferenceModeForm({ input, onChange }: ReferenceModeFormProps) {
  const { characters } = useCharacterStore()
  const { savedPlots } = usePlotStore()
  const { savedRelationships } = useRelationshipStore()
  const { savedWikipedias } = useWikipediaStore()
  const { savedNames } = useNameStore()
  const { savedTitles } = useTitleStore()
  const { enemies: savedEnemies, fixDuplicateIds } = useEnemyStore()
  
  // コンポーネントマウント時に重複IDをチェックして修正
  useEffect(() => {
    const hasDuplicates = fixDuplicateIds()
    if (hasDuplicates) {
      console.log('エネミーIDの重複を修正しました')
    }
  }, [fixDuplicateIds])
  
  // 初期ロード時に既存の参照データからコンテキストを構築
  useEffect(() => {
    if (input.references && Object.keys(input.references).length > 0) {
      const context = buildCompleteContext(input.references)
      if (context && !input.referenceContext) {
        onChange({
          ...input,
          referenceContext: context,
        })
      }
    }
  }, [])
  
  const buildCompleteContext = (refs: any) => {
    const contexts: string[] = []
    
    // プロット（詳細版）
    if (refs?.savedPlotId) {
      const plot = savedPlots.find(p => p.id === refs.savedPlotId)
      if (plot) {
        contexts.push(generatePlotContext(plot))
      }
    }
    
    // キャラクター（詳細版）
    if (refs?.savedCharacterIds && refs.savedCharacterIds.length > 0) {
      const selectedChars = characters.filter(c => refs.savedCharacterIds?.includes(c.id))
      if (selectedChars.length > 0) {
        contexts.push(`【保存済みキャラクター（${selectedChars.length}名）から世界観を構築】\n`)
        selectedChars.forEach(char => {
          contexts.push(generateCharacterContext(char))
        })
      }
    }
    
    // 関係性（詳細版）
    if (refs?.savedRelationshipId) {
      const rel = savedRelationships.find(r => r.id === refs.savedRelationshipId)
      if (rel) {
        contexts.push(generateRelationshipContext(rel))
      }
    }
    
    // エネミー（詳細版）
    if (refs?.savedEnemyIds && refs.savedEnemyIds.length > 0) {
      const selectedEnemies = savedEnemies.filter(e => refs.savedEnemyIds?.includes(e.id))
      if (selectedEnemies.length > 0) {
        contexts.push(`【保存済みエネミー・脅威（${selectedEnemies.length}体）から世界観を構築】\n`)
        selectedEnemies.forEach(enemy => {
          contexts.push(generateEnemyContext(enemy))
        })
      }
    }
    
    // Wikipedia（詳細版）
    if (refs?.savedWikipediaIds && refs.savedWikipediaIds.length > 0) {
      const selectedWikis = savedWikipedias.filter(w => refs.savedWikipediaIds?.includes(w.id))
      if (selectedWikis.length > 0) {
        contexts.push(`【保存済み架空事典項目（${selectedWikis.length}項目）から世界観を構築】\n`)
        selectedWikis.forEach(wiki => {
          contexts.push(generateWikipediaContext(wiki))
        })
      }
    }
    
    // 名前
    if (refs?.savedNameIds && refs.savedNameIds.length > 0) {
      const selectedNames = savedNames.filter(n => refs.savedNameIds?.includes(n.id))
      if (selectedNames.length > 0) {
        const namesByType: Record<string, string[]> = {}
        selectedNames.forEach(n => {
          const type = n.nameType || 'その他'
          if (!namesByType[type]) namesByType[type] = []
          namesByType[type].push(n.name)
        })
        const nameContext = Object.entries(namesByType).map(([type, names]) => 
          `${type}: ${names.join('、')}`
        ).join('\n')
        contexts.push(`【使用する名前（${selectedNames.length}個）】\n${nameContext}`)
      }
    }
    
    // タイトル
    if (refs?.savedTitleIds && refs.savedTitleIds.length > 0) {
      const selectedTitles = savedTitles.filter(t => refs.savedTitleIds?.includes(t.id))
      if (selectedTitles.length > 0) {
        const titleContext = selectedTitles.map(t => 
          `・${t.result.candidates?.[0]?.title || t.title}`
        ).join('\n')
        contexts.push(`【タイトル案（${selectedTitles.length}個）】\n${titleContext}`)
      }
    }
    
    return contexts.join('\n\n')
  }
  
  const handleReferenceChange = (type: string, value: string | string[]) => {
    const references = { ...input.references }
    
    // 参照データを更新
    const newReferences = {
      ...references,
      [type === 'plot' ? 'savedPlotId' : 
       type === 'characters' ? 'savedCharacterIds' :
       type === 'relationship' ? 'savedRelationshipId' :
       type === 'wikipedias' ? 'savedWikipediaIds' :
       type === 'names' ? 'savedNameIds' :
       type === 'titles' ? 'savedTitleIds' :
       'savedEnemyIds']: value,
    }
    
    // 完全なコンテキストを構築（更新された参照を使用）
    const newContext = buildCompleteContext(newReferences)
    
    onChange({
      ...input,
      references: newReferences,
      referenceContext: newContext,
    })
  }
  
  return (
    <div className="space-y-4">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          保存済みの他のジェネレータのデータから世界観を構築します。
          選択したデータを分析して、AIが最適な世界観パラメータを自動決定します。
        </AlertDescription>
      </Alert>
      
      {savedPlots.length > 0 && (
        <div>
          <Label>プロットから生成</Label>
          <Select
            value={input.references?.savedPlotId || ''}
            onValueChange={(value) => handleReferenceChange('plot', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="プロットを選択..." />
            </SelectTrigger>
            <SelectContent>
              {savedPlots.map((plot) => (
                <SelectItem key={plot.id} value={plot.id}>
                  {plot.title} - {plot.result.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {characters.length > 0 && (
        <div>
          <Label>キャラクターから生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {characters.map((char) => (
              <label
                key={char.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedCharacterIds?.includes(char.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedCharacterIds || []
                    const newIds = e.target.checked
                      ? [...ids, char.id]
                      : ids.filter(id => id !== char.id)
                    handleReferenceChange('characters', newIds)
                  }}
                />
                <span className="text-sm">{char.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      {savedRelationships.length > 0 && (
        <div>
          <Label>関係性から生成</Label>
          <Select
            value={input.references?.savedRelationshipId || ''}
            onValueChange={(value) => handleReferenceChange('relationship', value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="関係性を選択..." />
            </SelectTrigger>
            <SelectContent>
              {savedRelationships.map((rel) => (
                <SelectItem key={rel.id} value={rel.id}>
                  {rel.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {savedEnemies.length > 0 && (
        <div>
          <Label>エネミー・怪異から生成（複数選択可）</Label>
          <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
            {savedEnemies.map((enemy) => (
              <label
                key={enemy.id}
                className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={input.references?.savedEnemyIds?.includes(enemy.id) || false}
                  onChange={(e) => {
                    const ids = input.references?.savedEnemyIds || []
                    const newIds = e.target.checked
                      ? [...ids, enemy.id]
                      : ids.filter(id => id !== enemy.id)
                    handleReferenceChange('enemies', newIds)
                  }}
                />
                <span className="text-sm">{enemy.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      
      <div className="space-y-2 border-t pt-4">
        <div className="flex items-center space-x-2">
          <Settings className="h-4 w-4" />
          <Label>追加設定（オプション）</Label>
        </div>
        <Textarea
          value={input.ideaText || ''}
          onChange={(e) => onChange({ ...input, ideaText: e.target.value })}
          placeholder="例：&#10;・中世ヨーロッパ風の街並みを含めて&#10;・魔法は存在するが一般人には陰されている&#10;・人間以外の種族も共存している&#10;・古代文明の遺跡が点在している"
          rows={4}
        />
        <p className="text-xs text-muted-foreground">
          参照データに加えて、世界観に含めたい要素や詳細な指示を追加できます
        </p>
      </div>
    </div>
  )
}